import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BookOpen, Compass, GraduationCap, CheckCircle2, Circle, ArrowRight,
  Briefcase, Layers, MessageSquare,
} from 'lucide-react';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_HUE = {
  regular: '#22d3ee',
  honors: '#38bdf8',
  AP: '#a78bfa',
};

export default function AcademicAlignmentPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/academic-alignment`, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  if (loading) {
    return <AppLayout><div className="py-24 flex justify-center"><LoadingSpinner /></div></AppLayout>;
  }
  if (!data) {
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto text-center py-16">
          <p className="text-slate-300">Couldn't load your academic alignment.</p>
        </div>
      </AppLayout>
    );
  }

  const { current_direction, school, requirements, recommended_prep } = data;
  const dirName = current_direction?.career_interest || 'a direction';
  const dirCluster = current_direction?.cluster;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-12" data-testid="academic-alignment-page">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-6 md:p-8"
          style={{ boxShadow: '0 40px 100px -60px rgba(34,211,238,0.6)' }}
        >
          <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full blur-3xl opacity-30"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute -left-24 -bottom-32 h-72 w-72 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.35), transparent 65%)' }} />

          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
              text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
              <Compass className="h-3 w-3" /> Academic Alignment
            </span>
            <h1 className="font-outfit mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              Preparation for{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">
                {dirName}
              </span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300/90 leading-relaxed">
              Here's how your school's course options line up against a{' '}
              {dirName !== 'a direction' ? dirName : 'career-ready'} direction. Your counselor advises,
              your school handles enrollment, this is the intelligence layer between the two.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              {dirCluster && (
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-cyan-300" />
                  <span className="text-slate-300">NC Career Cluster:</span>
                  <span className="text-white">{dirCluster}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5 text-violet-300" />
                <span className="text-slate-300">School:</span>
                <span className="text-white">{school.name}</span>
                {school.is_synthetic && (
                  <span className="ml-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-200">
                    demo data
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/app/nc-cdp"
                data-testid="alignment-open-cdp"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
                style={{ boxShadow: '0 0 26px -8px rgba(34,211,238,0.6)' }}
              >
                Open my CDP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/app/counselor-meetings"
                data-testid="alignment-book-counselor"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-violet-300/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20 transition-colors"
              >
                <MessageSquare className="h-4 w-4" /> Talk to my counselor
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Recommended prep buckets */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-outfit text-lg font-semibold text-white">Recommended preparation</h2>
            <span className="text-xs text-slate-300">Matched to courses at {school.name}</span>
          </div>

          {recommended_prep.length === 0 && (
            <div className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-6 text-sm text-slate-300">
              We don't have a preparation blueprint for this direction yet. Talk to your counselor,               they can seed one directly into your plan.
            </div>
          )}

          {recommended_prep.map((bucket, i) => (
            <motion.div
              key={bucket.key}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={`prep-bucket-${bucket.key}`}
              className="rounded-2xl border border-cyan-500/20 bg-slate-900/40 p-5"
              style={{ boxShadow: '0 26px 60px -50px rgba(34,211,238,0.5)' }}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-cyan-300" />
                    <h3 className="text-base font-semibold text-white">{bucket.label}</h3>
                    {bucket.available_at_school ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-cyan-200">
                        <CheckCircle2 className="h-3 w-3" /> Available
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-200">
                        <Circle className="h-3 w-3" /> Not offered here
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-slate-300/90 max-w-2xl">{bucket.why}</p>
                </div>
              </div>

              {bucket.matches.length > 0 ? (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {bucket.matches.map((c) => {
                    const hue = LEVEL_HUE[c.level] || '#22d3ee';
                    return (
                      <div key={c.id} className="rounded-xl border p-3"
                        style={{ borderColor: `${hue}33`, background: `${hue}0a` }}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider" style={{ color: hue }}>{c.level}</span>
                          <span className="text-[10px] text-slate-300">· {c.credits} credit</span>
                        </div>
                        <p className="mt-0.5 text-sm font-medium text-white">{c.name}</p>
                        <p className="text-[11px] text-slate-300 mt-0.5">
                          {c.department} · grade {c.grade_range?.[0]}-{c.grade_range?.[1]}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-3 text-xs text-amber-200/80">
                  Nothing in the {school.name} catalog matches this bucket. Your counselor can suggest
                  a dual-enrollment / Wake Tech CCP alternative.
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* WCPSS graduation requirements, reference block */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-violet-400/25 bg-[#0a1729]/60 p-6"
          style={{ boxShadow: '0 30px 80px -60px rgba(167,139,250,0.5)' }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-outfit text-lg font-semibold text-white">Wake County graduation requirements</h2>
              <p className="text-xs text-slate-300 mt-0.5">
                WCPSS Future-Ready Core · {requirements.total_credits_required} credits total
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {requirements.buckets.map((b) => (
              <div key={b.key} className="rounded-xl border border-cyan-300/20 bg-cyan-400/[0.05] p-3">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-semibold text-white">{b.label}</p>
                  <p className="text-lg font-bold text-cyan-200">{b.credits}</p>
                </div>
                <p className="mt-1 text-[11px] text-slate-300 leading-relaxed">{b.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-slate-300">
            <Briefcase className="h-3.5 w-3.5 text-cyan-300" />
            <span>
              For the NC Career Endorsement, a 4-course concentration in one CTE cluster (like Engineering
              or IT) + industry credential + 40+ WBL hours is expected.
            </span>
          </div>
        </motion.div>
      </div>
    </AppLayout>
  );
}
